import {
  getAccessTokenFromGhCookie,
  getAccessTokenFromGhState,
  getGhComponent,
  hasGhRefreshCredential,
  requestGhAccessTokenRefresh,
  verifyBackendSessionToken,
} from '@/lib/ghAuth';
import { useAuthStore } from '@/stores/AuthStore';

const GH_REFRESH_TIMEOUT_MS = 10000;
const BACKEND_VERIFICATION_TIMEOUT_MS = 10000;

type BootstrapCallbacks = {
  onLoading: (message: string) => void;
  onError: (message: string) => void;
  onResolved: () => void;
};

type AuthSnapshot = {
  status: 'authenticated' | 'anonymous' | 'unknown';
  accessToken: string | null;
  explicitAnonymous: boolean;
};

export type WebAuthBootstrapController = {
  retry: () => void;
  dispose: () => void;
};

const COMPONENT_WAIT_MESSAGE = 'Connecting to Gaming Hub to secure your session.';
const INITIALIZATION_MESSAGE = 'Checking your Gaming Hub session.';
const VERIFICATION_MESSAGE = 'Verifying your Dream Team session.';
const REFRESH_MESSAGE = 'Refreshing your Dream Team session.';

function normalizeSnapshot(
  authState: GhAuthState | null | undefined,
  ghComponent: GhComponentApi,
  options: { allowCookieFallback: boolean; explicitAnonymous: boolean }
): AuthSnapshot {
  const snapshotToken = getAccessTokenFromGhState(authState ?? undefined);
  const accessToken =
    snapshotToken ?? (options.allowCookieFallback ? getAccessTokenFromGhCookie(ghComponent) : null);
  return {
    status: accessToken
      ? 'authenticated'
      : authState?.status === 'anonymous'
        ? 'anonymous'
        : authState?.status === 'authenticated'
          ? 'authenticated'
          : 'unknown',
    accessToken,
    explicitAnonymous: options.explicitAnonymous && !snapshotToken,
  };
}

function getSnapshotFingerprint(snapshot: AuthSnapshot): string {
  return JSON.stringify([snapshot.status, snapshot.accessToken, snapshot.explicitAnonymous]);
}

export function startWebAuthBootstrap(callbacks: BootstrapCallbacks): WebAuthBootstrapController {
  let disposed = false;
  let released = false;
  let ghComponent: GhComponentApi | null = null;
  let unsubscribeAuth: (() => void) | undefined;
  let refreshTimeout: number | undefined;
  let initializationPromise: Promise<GhAuthState | null | undefined> | null = null;
  let initialized = false;
  let latestSnapshot: AuthSnapshot | null = null;
  let latestFingerprint: string | null = null;
  let revision = 0;
  let resolutionPromise: Promise<number> | null = null;
  let retryRequested = false;
  const backendAbortControllers = new Set<AbortController>();

  const clearTimer = (timer: number | undefined) => {
    if (timer !== undefined) window.clearTimeout(timer);
  };

  const showLoading = (message: string) => {
    if (!disposed && !released) callbacks.onLoading(message);
  };

  const showError = (message: string) => {
    if (!disposed && !released) callbacks.onError(message);
  };

  const settleAuthenticated = (token: string, userId: string) => {
    if (disposed) return;
    useAuthStore.getState().resolveAuthenticatedSession({ token, userId });
    if (!released) {
      released = true;
      callbacks.onResolved();
    }
  };

  const settleAnonymous = () => {
    if (disposed) return;
    useAuthStore.getState().resolveAnonymousSession();
    if (!released) {
      released = true;
      callbacks.onResolved();
    }
  };

  const promoteRefreshedToken = (token: string): number => {
    const snapshot: AuthSnapshot = {
      status: 'authenticated',
      accessToken: token,
      explicitAnonymous: false,
    };
    const fingerprint = getSnapshotFingerprint(snapshot);
    if (fingerprint !== latestFingerprint) {
      latestSnapshot = snapshot;
      latestFingerprint = fingerprint;
      revision += 1;
    }
    return revision;
  };

  const verifyToken = async (token: string) => {
    const controller = new AbortController();
    backendAbortControllers.add(controller);
    const timeout = window.setTimeout(() => controller.abort(), BACKEND_VERIFICATION_TIMEOUT_MS);

    try {
      return await verifyBackendSessionToken(token, controller.signal);
    } finally {
      window.clearTimeout(timeout);
      backendAbortControllers.delete(controller);
    }
  };

  const refreshToken = async (rejectedToken: string | null, expectedRevision: number) => {
    if (!ghComponent) return { kind: 'stale' as const };

    showLoading(REFRESH_MESSAGE);
    clearTimer(refreshTimeout);
    refreshTimeout = window.setTimeout(() => {
      showError('Gaming Hub is taking too long to refresh your session. Please try again.');
    }, GH_REFRESH_TIMEOUT_MS);

    const token = await requestGhAccessTokenRefresh(ghComponent, rejectedToken);
    clearTimer(refreshTimeout);
    refreshTimeout = undefined;

    if (disposed || revision !== expectedRevision) {
      return { kind: 'stale' as const };
    }
    if (!token) {
      return hasGhRefreshCredential(ghComponent)
        ? { kind: 'failure' as const }
        : { kind: 'anonymous' as const };
    }

    return { kind: 'refreshed' as const, token, revision: promoteRefreshedToken(token) };
  };

  const resolveLatestSnapshot = async (): Promise<number> => {
    while (!disposed) {
      const snapshot = latestSnapshot;
      const expectedRevision = revision;
      if (!snapshot || !ghComponent) {
        showError('Gaming Hub could not provide an authentication state. Please try again.');
        return expectedRevision;
      }

      if (snapshot.explicitAnonymous) {
        settleAnonymous();
        return expectedRevision;
      }

      let token = snapshot.accessToken;
      let verificationRevision = expectedRevision;
      let tokenWasRefreshed = false;

      if (!token) {
        if (!hasGhRefreshCredential(ghComponent)) {
          if (snapshot.status === 'anonymous') {
            settleAnonymous();
          } else {
            showError(
              'Gaming Hub could not provide credentials for this session. Please try again.'
            );
          }
          return expectedRevision;
        }

        const refreshResult = await refreshToken(null, expectedRevision);
        if (refreshResult.kind === 'stale') continue;
        if (refreshResult.kind === 'anonymous') {
          settleAnonymous();
          return expectedRevision;
        }
        if (refreshResult.kind === 'failure') {
          showError('Gaming Hub could not refresh your session. Please try again.');
          return expectedRevision;
        }

        token = refreshResult.token;
        verificationRevision = refreshResult.revision;
        tokenWasRefreshed = true;
      }

      showLoading(VERIFICATION_MESSAGE);
      let verification = await verifyToken(token);
      if (disposed) return verificationRevision;
      if (revision !== verificationRevision) continue;

      if (verification.kind === 'authenticated') {
        settleAuthenticated(token, verification.userId);
        return verificationRevision;
      }
      if (verification.kind === 'failure') {
        showError(
          'Dream Team could not verify your session. Please check your connection and try again.'
        );
        return verificationRevision;
      }
      if (tokenWasRefreshed || !hasGhRefreshCredential(ghComponent)) {
        settleAnonymous();
        return verificationRevision;
      }

      const refreshResult = await refreshToken(token, verificationRevision);
      if (refreshResult.kind === 'stale') continue;
      if (refreshResult.kind === 'anonymous') {
        settleAnonymous();
        return verificationRevision;
      }
      if (refreshResult.kind === 'failure') {
        showError('Gaming Hub could not refresh your session. Please try again.');
        return verificationRevision;
      }

      token = refreshResult.token;
      verificationRevision = refreshResult.revision;
      showLoading(VERIFICATION_MESSAGE);
      verification = await verifyToken(token);
      if (disposed) return verificationRevision;
      if (revision !== verificationRevision) continue;

      if (verification.kind === 'authenticated') {
        settleAuthenticated(token, verification.userId);
      } else if (verification.kind === 'unauthorized') {
        settleAnonymous();
      } else {
        showError(
          'Dream Team could not verify your session. Please check your connection and try again.'
        );
      }
      return verificationRevision;
    }

    return revision;
  };

  const runResolution = () => {
    if (disposed) return;
    if (resolutionPromise) return;

    let completedRevision = revision;
    const currentPromise = resolveLatestSnapshot();
    resolutionPromise = currentPromise;
    void currentPromise
      .then(processedRevision => {
        completedRevision = processedRevision;
      })
      .finally(() => {
        if (resolutionPromise === currentPromise) resolutionPromise = null;
        if (disposed) return;

        if (retryRequested) {
          retryRequested = false;
          runResolution();
          return;
        }

        if (revision !== completedRevision) runResolution();
      });
  };

  const handleAuthSnapshot = (authState: GhAuthState, allowCookieFallback: boolean) => {
    if (disposed || !ghComponent) return;

    const snapshot = normalizeSnapshot(authState, ghComponent, {
      allowCookieFallback,
      explicitAnonymous: !allowCookieFallback && authState.status === 'anonymous',
    });
    const fingerprint = getSnapshotFingerprint(snapshot);
    if (fingerprint === latestFingerprint) return;

    latestSnapshot = snapshot;
    latestFingerprint = fingerprint;
    revision += 1;
    if (initialized) runResolution();
  };

  const registerSubscription = () => {
    if (!ghComponent || unsubscribeAuth) return;

    let isImmediateReplay = true;
    const unsubscribe = ghComponent.subscribeAuth?.(authState => {
      handleAuthSnapshot(authState, isImmediateReplay);
    });
    isImmediateReplay = false;
    if (typeof unsubscribe === 'function') unsubscribeAuth = unsubscribe;
  };

  const initializeGamingHub = () => {
    if (disposed || !ghComponent) return;
    if (initialized) {
      runResolution();
      return;
    }
    if (initializationPromise) {
      return;
    }

    showLoading(INITIALIZATION_MESSAGE);
    initializationPromise = Promise.resolve().then(() => ghComponent?.initAuth());

    void initializationPromise
      .then(initialAuthState => {
        initializationPromise = null;
        if (disposed || !ghComponent) return;

        showLoading(VERIFICATION_MESSAGE);
        initialized = true;
        latestSnapshot = normalizeSnapshot(initialAuthState, ghComponent, {
          allowCookieFallback: true,
          explicitAnonymous: false,
        });
        latestFingerprint = getSnapshotFingerprint(latestSnapshot);
        registerSubscription();
        runResolution();
      })
      .catch(() => {
        initializationPromise = null;
        showError('Gaming Hub could not initialize your session. Please try again.');
      });
  };

  const connectToGamingHub = () => {
    if (disposed || ghComponent) return;
    const availableComponent = getGhComponent();
    if (!availableComponent) {
      showLoading(COMPONENT_WAIT_MESSAGE);
      return;
    }

    ghComponent = availableComponent;
    initializeGamingHub();
  };

  const handleComponentReady = () => connectToGamingHub();
  window.addEventListener('ghComponent-ready', handleComponentReady);
  connectToGamingHub();

  return {
    retry: () => {
      if (disposed || released) return;
      showLoading(COMPONENT_WAIT_MESSAGE);

      if (!ghComponent) {
        connectToGamingHub();
      } else if (!initialized) {
        initializeGamingHub();
      } else if (resolutionPromise) {
        retryRequested = true;
      } else {
        runResolution();
      }
    },
    dispose: () => {
      disposed = true;
      clearTimer(refreshTimeout);
      window.removeEventListener('ghComponent-ready', handleComponentReady);
      unsubscribeAuth?.();
      backendAbortControllers.forEach(controller => controller.abort());
      backendAbortControllers.clear();
    },
  };
}
